import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  MenuItem,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { axiosService } from "../services/axios.service";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import { UserService } from "../services/user.service";
// import { ProductOrServiceId } from '../data/products-and-services';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 800,
  margin: "2rem auto",
}));

interface PaymentResponse {
  checkout_url: string;
}

interface PaymentFormData {
  amount: number;
  currency: string;
  customerEmail: string;
  customerName: string;
  billingCountry: string;
  billingState: string;
  billingCity: string;
  billingAddress: string;
  billingZip: string;
  billingPhone: string;
  product: string;
  productRequestId: string;
}

interface FormErrors {
  customerEmail?: string;
  billingPhone?: string;
}

interface BillingInfo {
  billingCountry?: string;
  billingState?: string;
  billingCity?: string;
  billingAddress?: string;
  billingZip?: string;
  billingPhone?: string;
}

const worldCardCurrencies = [{ value: "CAD", label: "Canadian Dollar" }];

const bridgerPayCurrencies = [{ value: "USD", label: "US Dollar" }];

const tranzilaCurrencies = [{ value: "USD", label: "US Dollar" }];

// Validation patterns
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_PATTERN = /^\+?[1-9]\d{1,14}$/; // International phone number format

const PaymentPage: React.FC = () => {
  const [provider, setProvider] = useState("bridgerpay");
  const [currencies, setCurrencies] = useState(bridgerPayCurrencies);
  const [productRequests, setProductRequests] = useState<any[]>([]);
  const [selectedProductRequest, setSelectedProductRequest] =
    useState<any>(null);

  const [formData, setFormData] = useState<PaymentFormData>({
    amount: 0,
    currency: currencies[0].value,
    customerEmail: "",
    customerName: "",
    billingCountry: "",
    billingState: "",
    billingCity: "",
    billingAddress: "",
    billingZip: "",
    billingPhone: "",
    product: "",
    productRequestId: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { paymentProductId } = useParams();
  const [error, setError] = useState<string | null>(null);

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const email = user?.email || "";
    const name = user?.displayName || "";
    let amount = 0;

    if (paymentProductId && productRequests.length) {
      const product = productRequests.find(
        (request) => request.fullData.id === paymentProductId
      );
      amount = product?.leftToPay || 0;
    }

    setFormData(prev => ({
      ...prev,
      customerName: name,
      customerEmail: email,
      amount,
    }));
  }, [user?.email, user?.displayName, paymentProductId, productRequests]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
    if (user) {
      initProductRequests();
      fetchBillingInfo();
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (provider) {
      if (provider === "worldcard") {
        setCurrencies(worldCardCurrencies);
        setFormData((prev) => ({
          ...prev,
          currency: worldCardCurrencies[0].value,
        }));
      } else if (provider === "tranzila") {
        setCurrencies(tranzilaCurrencies);
        setFormData((prev) => ({
          ...prev,
          currency: tranzilaCurrencies[0].value,
        }));
      } else {
        setCurrencies(bridgerPayCurrencies);
        setFormData((prev) => ({
          ...prev,
          currency: bridgerPayCurrencies[0].value,
        }));
      }
    }
  }, [provider]);

  const fetchBillingInfo = async () => {
    try {
      const response = await axiosService.get<BillingInfo>('user/billing-info');
      
      // Pre-populate form with billing info
      if (response) {
        setFormData(prev => ({
          ...prev,
          billingCountry: response.billingCountry || '',
          billingState: response.billingState || '',
          billingCity: response.billingCity || '',
          billingAddress: response.billingAddress || '',
          billingZip: response.billingZip || '',
          billingPhone: response.billingPhone || '',
        }));
      }
    } catch (error) {
      console.log("Error fetching billing info: ", error);
      // Don't throw error, just log it since billing info is optional
    }
  };

  const initProductRequests = async () => {
    try {
      setLoading(true);
      const response = await UserService.getUserProductRequests(
        null,
        null,
        null,
        "waiting for actions",
        true
      );

      const fetchedRequests = (response?.data ?? []) as any[];

      if (fetchedRequests && fetchedRequests.length > 0) {
        const mappedRequests = fetchedRequests.map((request) => {
          return {
            label: `${request.productRequestType} - Total: ${request.totalAmount}$ - Left To Pay: ${request.leftToPay}$ `,
            value: request.id,
            leftToPay: request.leftToPay,
            product: request.productRequestType,
            fullData: request,
          };
        });
        setProductRequests(mappedRequests);
      }
    } catch (error) {
      console.log("Error in ClientDashboardPage: ", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case "customerEmail":
        if (!value) return "Email is required";
        if (!EMAIL_PATTERN.test(value))
          return "Please enter a valid email address";
        return undefined;
      case "billingPhone":
        if (!value) return "Phone number is required";
        if (!PHONE_PATTERN.test(value))
          return "Please enter a valid phone number (e.g., +1234567890)";
        return undefined;
      default:
        return undefined;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "productRequestId") {
      const selectedRequest = productRequests.find(
        (req) => req.value === value
      );
      setSelectedProductRequest(selectedRequest);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        amount: selectedRequest ? selectedRequest.leftToPay : 0,
        product: selectedRequest ? selectedRequest.product : "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate the field and update errors
    const error = validateField(name, value);
    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    // Validate email
    const emailError = validateField("customerEmail", formData.customerEmail);
    if (emailError) {
      errors.customerEmail = emailError;
      isValid = false;
    }

    // Validate phone
    const phoneError = validateField("billingPhone", formData.billingPhone);
    if (phoneError) {
      errors.billingPhone = phoneError;
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const getTwoChars = (input: string) => {
    const str = input.trim();
    if (str.length === 0) return "--";
    if (str.length === 1) return (str + "-").toUpperCase();
    return str.slice(0, 2).toUpperCase();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!validateForm() && provider !== "tranzila") {
      setLoading(false);
      return;
    }

    try {
      if (formData.amount <= 0) {
        throw new Error("Please enter a valid amount");
      }

      if (!user) {
        throw new Error("You must be logged in to make a payment");
      }

      const returnUrl = `${window.location.protocol}//${window.location.host}/payment/success`;
      const cancelUrl = `${window.location.protocol}//${window.location.host}/payment/cancel`;

      const requestPayload = {
        ...formData,
        billingState: getTwoChars(formData.billingState),
        returnUrl,
        cancelUrl,
      };

      const response = await axiosService.post<PaymentResponse>(
        `/payment/create?provider=${provider}`,
        requestPayload
      );

      if (response.checkout_url) {
        // Save form data and provider before redirecting (for retry functionality)
        const paymentRetryData = {
          formData: requestPayload,
          provider,
          timestamp: Date.now(),
          productRequestId: formData.productRequestId,
        };
        localStorage.setItem('paymentRetryData', JSON.stringify(paymentRetryData));
        
        window.location.href = response.checkout_url;
      } else {
        throw new Error("No payment URL received from server");
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while processing your payment"
      );
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null; // Will redirect to login due to useEffect
  }

  return (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <Box
          sx={{
            position: "fixed",
            zIndex: 9999,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      <StyledPaper elevation={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Payment Information
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Select Payment Method</FormLabel>
          <RadioGroup
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value);
            }}
            row
          >
            {/* <FormControlLabel
              value="worldcard"
              control={<Radio />}
              label="Interac"
            /> */}
            <FormControlLabel
              value="bridgerpay"
              control={<Radio />}
              label="Bridger Pay"
              // disabled
            />
           {/* <FormControlLabel
              value="tranzila"
              control={<Radio />}
              label="Tranzila"
            /> */}
          </RadioGroup>
        </FormControl>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {!paymentProductId && (
                <TextField
                  fullWidth
                  select
                  label="Product Request"
                  name="productRequestId"
                  value={formData.productRequestId}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                >
                  {productRequests.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                required
                inputProps={{
                  min: "0.01",
                  step: "0.01",
                  max: selectedProductRequest?.leftToPay || undefined,
                }}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                {currencies.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="customerEmail"
                type="email"
                value={formData.customerEmail}
                onChange={handleInputChange}
                required
                disabled={loading}
                error={!!formErrors?.customerEmail}
                helperText={formErrors?.customerEmail}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="billingAddress"
                value={formData.billingAddress}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                name="billingCity"
                value={formData.billingCity}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ZIP/Postal Code"
                name="billingZip"
                value={formData.billingZip}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="billingPhone"
                type="tel"
                value={formData.billingPhone}
                onChange={handleInputChange}
                required
                disabled={loading}
                error={!!formErrors?.billingPhone}
                helperText={formErrors?.billingPhone || "Format: +1234567890"}
                placeholder="+1234567890"
              />
            </Grid>

            {provider !== "tranzila" && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="State/Province"
                    name="billingState"
                    value={formData.billingState}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="billingCountry"
                    value={formData.billingCountry}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
            disabled={loading}
          >
            Pay Now
          </Button>
        </form>
      </StyledPaper>
    </Box>
  );
};

export default PaymentPage;
